import { Injectable, Inject, PLATFORM_ID, inject } from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import axios, { AxiosInstance } from 'axios';
import { KeycloakAuthService } from '../core/services/keycloak-auth.service';
import { ApiConfigService } from '../core/services/api-config.service';

export interface PresignedUrlItem {
  S3Key: string;
  Url: string;
  ExpiresIn: number;
}

export interface PresignedUrlResponse {
  Items: PresignedUrlItem[];
}

@Injectable({
  providedIn: 'root'
})
export class StorageService {
  private authService = inject(KeycloakAuthService);
  private apiConfig = inject(ApiConfigService);
  private api: AxiosInstance;

  constructor(@Inject(PLATFORM_ID) private platformId: Object) {
    this.api = axios.create({
      headers: {
        'Content-Type': 'application/json'
      }
    });

    this.api.interceptors.request.use((config) => {
      if (isPlatformBrowser(this.platformId)) {
        const baseUrl = this.apiConfig.getApiBaseUrl();
        const tenantId = this.apiConfig.getTenantId();
        config.baseURL = `${baseUrl}/v2/${tenantId}`;
      }

      const token = this.authService.accessToken;
      if (token) {
        config.headers.Authorization = `Bearer ${token}`;
      }
      return config;
    });
  }

  async generatePresignedUrl(integrationId: string): Promise<PresignedUrlResponse> {
    try {
      const baseUrl = this.apiConfig.getApiBaseUrl();
      const tenantId = this.apiConfig.getTenantId();
      const response = await this.api.post(
        `${baseUrl}/v2/${tenantId}/${integrationId}/storage/generatepresignedurl`,
        {}
      );
      return response.data;
    } catch (error) {
      console.error('Failed to generate presigned URL:', error);
      throw error;
    }
  }

  async getFile(integrationId: string, fileId: string): Promise<Blob | null> {
    try {
      // The API now returns the raw file bytes (no lambda/base64 wrapping), so we must
      // fetch it as an arraybuffer instead of letting axios attempt to JSON-parse it.
      const response = await this.api.get(
        `/${integrationId}/storage/${fileId}`,
        { responseType: 'arraybuffer' }
      );

      if (!response.data) {
        return null;
      }

      const contentType = response.headers['content-type'] || 'application/pdf';
      return new Blob([response.data], { type: contentType });
    } catch (error) {
      console.error('Failed to fetch file from storage:', error);
      throw error;
    }
  }
}
